import { isCrossOriginIframe } from '../browser/isCrossOriginIframe';

/**
 * Extra attributes for cookies written from a cross-origin iframe over HTTPS.
 *
 * Only cross-origin embeds need them. Same-origin iframes (e.g. `widgets_iframe`) must NOT
 * be partitioned, or their cookies would live in a different partition than the top-level
 * document and desync from it.
 *
 * Without `SameSite=None; Secure` the browser treats the cookie as `SameSite=Lax` and
 * silently drops the write in a cross-site iframe (e.g. MS Teams); `Partitioned` (CHIPS)
 * keeps it working when third-party cookies are blocked. `Secure` requires HTTPS, so this
 * is skipped on `http://localhost`. `setCookie` and `removeCookie` share these so a delete
 * targets the same cookie partition as the write — necessary, but not sufficient on its own:
 * name, path and the `Domain`/host-only flag must also match for the browser to remove it
 * (`setCookie` writes host-only cookies, `removeCookie` sets a `Domain`).
 */
const getCrossSiteCookieAttributes = () =>
    isCrossOriginIframe() && window.location.protocol === 'https:' ? '; SameSite=None; Secure; Partitioned' : '';

/**
 * Get the value of the cookie passed as argument.
 * @param  {string}  cookieName The name of the cookie you need to get.
 * @return {string} The value of the cookie. If no cookie for this name, returns `undefined`.
 */
export const getCookie = (cookieName: string) => {
    const cookiePair = document.cookie.split(';').find((cookie) => cookie.trim().startsWith(`${cookieName}`));

    return cookiePair ? cookiePair.split('=')[1] : undefined;
};

/**
 * Set a cookie.
 * @param {string}      cookieName  The cookie identifier.
 * @param {string}      cookieValue The cookie value.
 * @param {string|Date} expires     The expire date if the cookie.
 * @param {string}      path        The path of the cookie.
 */
export const setCookie = (cookieName: string, cookieValue: string, expires: string | Date, path = '/') => {
    const expireDate = typeof expires === 'string' ? expires : expires.toUTCString();
    document.cookie = `${cookieName}=${cookieValue}; expires=${expireDate}; path=${path}${getCrossSiteCookieAttributes()}`;
};

/**
 * Remove the cookie matching the name passed as argument.
 * @param {string} cookieName The name of the cookie you need to remove.
 * @param {string} path       The path of the cookie you need to remove.
 * @param {string} domain     The domain of the cookie you need to remove.
 */
export const removeCookie = (cookieName: string, path = '/', domain = window.location.hostname) => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}${getCrossSiteCookieAttributes()}`;
};
